import { createFileRoute, Link } from '@tanstack/react-router'
import { useState, useEffect, useMemo, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  queryKeys,
  useEvent,
  useEventHomePages, 
  useEventNavigations, 
  useEventCategories,
  useContentPages,
  useCreateEventHomePage,
  useCreateEventNavigation,
  useUpdateEventNavigation,
  useDeleteEventNavigation,
  useUploadNavigationImage,
  useCreateEventHomePageNavigation,
  useUpdateEventHomePageNavigation,
  useDeleteEventHomePageNavigation,
} from '@/lib/api/queries'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { 
  Plus, 
  Layout, 
  Trash2, 
  Edit2, 
  X, 
  Smartphone, 
  Grid, 
  Navigation, 
  Check,
  Search,
  Loader2,
} from 'lucide-react'
import { usePermission } from '@/lib/permissions'
import { AccessDenied } from '@/components/permission-guard'
import type { EventNavigation, EventHomePageNavigation, NavigationLinkType, EventPayFunction, ContentPage, EventCategory } from '@/lib/api/types'

export const Route = createFileRoute('/_authenticated/events/$eventId/home-layout')({
  component: HomeLayoutPage,
})

// EventPay function options
const EVENTPAY_FUNCTIONS: { value: EventPayFunction; label: string }[] = [
  { value: 'wallet', label: 'Wallet / Balance' },
  { value: 'vendors', label: 'Vendors List' },
  { value: 'orders', label: 'Order History' },
  { value: 'transactions', label: 'Transaction History' },
  { value: 'payment_methods', label: 'Payment Methods' },
  { value: 'edit_profile', label: 'Edit Profile' },
]

// Link type options
const LINK_TYPES: { value: NavigationLinkType; label: string }[] = [
  { value: 'external_url', label: 'External URL' },
  { value: 'content_page', label: 'Content Page' },
  { value: 'eventpay_function', label: 'EventPay Function' },
]

// Default form values
// Rows available in the home grid (editor, mobile preview, and the
// position picker all derive from this).
const GRID_MAX_ROWS = 10
const GRID_ROW_NUMBERS = Array.from({ length: GRID_MAX_ROWS }, (_, i) => i + 1)

const DEFAULT_FORM_VALUES = {
  title: '',
  link_type: 'external_url' as NavigationLinkType,
  external_url: '',
  content_page: null as number | null,
  eventpay_function: null as EventPayFunction | null,
  event_category: null as number | null,
  position_x: 1,
  position_y: 1,
  col_span: 1,
  row_span: 1,
  background_color: '#374151',
  text_color: '#ffffff',
  background_image: '',
  is_published: true,
  hide_title: false,
}

// Extended navigation item with grid position
interface NavigationWithPosition extends EventNavigation {
  gridId?: number // EventHomePageNavigation id
  position_x: number
  position_y: number
  col_span: number
  row_span: number
}

// Compact 2-column visual picker for placing a tile — replaces the abstract
// Column (X) / Row (Y) selects. Click a cell to set where the tile starts; the
// current footprint (col_span × row_span) fills in the tile's own colour, and
// cells taken by other tiles show as occupied.
function GridPositionPicker({
  posX,
  posY,
  colSpan,
  rowSpan,
  maxRows,
  items,
  editingId,
  onPick,
}: {
  posX: number
  posY: number
  colSpan: number
  rowSpan: number
  maxRows: number
  items: NavigationWithPosition[]
  editingId?: number | null
  onPick: (col: number, row: number) => void
}) {
  const rows = Array.from({ length: maxRows }, (_, i) => i + 1)
  const inFootprint = (c: number, r: number) =>
    c >= posX && c < posX + colSpan && r >= posY && r < posY + rowSpan
  const occupiedBy = (c: number, r: number) =>
    items.find(
      it =>
        it.id !== editingId &&
        c >= it.position_x &&
        c < it.position_x + (it.col_span || 1) &&
        r >= it.position_y &&
        r < it.position_y + (it.row_span || 1),
    )

  return (
    <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 p-3 w-full max-w-[400px]">
      <div className="grid gap-1.5 mb-1.5" style={{ gridTemplateColumns: '1rem 1fr 1fr' }}>
        <div />
        <div className="text-center text-[10px] font-medium uppercase tracking-wide text-neutral-400">Left</div>
        <div className="text-center text-[10px] font-medium uppercase tracking-wide text-neutral-400">Right</div>
      </div>
      <div className="space-y-1.5">
        {rows.map(r => (
          <div key={r} className="grid gap-1.5 items-center" style={{ gridTemplateColumns: '1rem 1fr 1fr' }}>
            <div className="text-[10px] text-neutral-400 text-right tabular-nums">{r}</div>
            {[1, 2].map(c => {
              const foot = inFootprint(c, r)
              const occ = !foot ? occupiedBy(c, r) : undefined
              const isStart = c === posX && r === posY
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => onPick(c, r)}
                  title={
                    occ
                      ? `Occupied by "${occ.title}"`
                      : `${c === 1 ? 'Left' : 'Right'} column, row ${r}`
                  }
                  className={`relative h-7 rounded-md border text-[9px] font-semibold uppercase tracking-wide transition-colors ${
                    foot
                      ? 'border-transparent text-white shadow-sm'
                      : occ
                        ? 'border-neutral-200 dark:border-neutral-600 bg-neutral-200/70 dark:bg-neutral-700 text-neutral-400'
                        : 'border-dashed border-neutral-300 dark:border-neutral-600 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                  }`}
                  style={foot ? { backgroundColor: '#272a30' } : undefined}
                >
                  {isStart ? 'Start' : occ ? '•' : ''}
                </button>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}

// Small live size gauges shown beside the Width / Height selects. Purple =
// width ("wide"), green = height ("tall") — matches the Image Guidelines colours.
function WidthGauge({ value }: { value: number }) {
  return (
    <span className="inline-flex gap-[3px] align-middle" aria-hidden="true">
      {[1, 2].map(i => (
        <span
          key={i}
          className={`h-4 w-3 rounded-[2px] border ${
            i <= value ? 'bg-purple-500 border-purple-500' : 'border-neutral-300 dark:border-neutral-600'
          }`}
        />
      ))}
    </span>
  )
}

function HeightGauge({ value, max }: { value: number; max: number }) {
  return (
    <span className="inline-flex flex-col gap-[3px] align-middle" aria-hidden="true">
      {Array.from({ length: max }, (_, i) => i + 1).map(i => (
        <span
          key={i}
          className={`w-4 h-1.5 rounded-[2px] border ${
            i <= value ? 'bg-green-500 border-green-500' : 'border-neutral-300 dark:border-neutral-600'
          }`}
        />
      ))}
    </span>
  )
}

function HomeLayoutPage() {
  const { eventId } = Route.useParams()
  const eventIdNum = Number(eventId)
  
  // Permission checks
  const canEditHomeLayout = usePermission('can_edit_home_layout')
  const canAddNavigationItem = usePermission('can_add_navigation_item')
  const canDeleteNavigationItem = usePermission('can_delete_navigation_item')
  
  // Data queries
  const { data: event } = useEvent(eventIdNum)
  const { data: homePagesData, isLoading: homePagesLoading } = useEventHomePages(eventIdNum, { 
    expand: 'navigations,navigations.navigation' 
  })
  const { isLoading: navigationsLoading } = useEventNavigations(eventIdNum)
  const { data: contentPagesData } = useContentPages(eventIdNum)
  
  // Mutations
  const createHomePage = useCreateEventHomePage()
  const createNavigation = useCreateEventNavigation()
  const updateNavigation = useUpdateEventNavigation()
  const deleteNavigation = useDeleteEventNavigation()
  const createHomePageNavigation = useCreateEventHomePageNavigation()
  const updateHomePageNavigation = useUpdateEventHomePageNavigation()
  const deleteHomePageNavigation = useDeleteEventHomePageNavigation()
  
  // State
  const [showAddModal, setShowAddModal] = useState(false)
  // Image picked while creating — uploaded right after the item exists
  // (the upload endpoint is per-navigation-id).
  const [pendingImage, setPendingImage] = useState<File | null>(null)
  const uploadNavigationImage = useUploadNavigationImage()
  const queryClient = useQueryClient()

  // The mutation hooks invalidate fire-and-forget, so the modal would close
  // BEFORE the grid refetch lands — the new/updated item pops in late (or
  // seemingly not at all on a slow connection). Awaiting the refetch keeps
  // the submit spinner up until the grid data actually contains the change.
  const refetchHomeLayout = () =>
    queryClient.refetchQueries({ queryKey: queryKeys.eventHomePages(eventIdNum) })
  const [showEditModal, setShowEditModal] = useState(false)
  const [showGuidelines, setShowGuidelines] = useState(false)
  const [editingItem, setEditingItem] = useState<NavigationWithPosition | null>(null)
  const [formData, setFormData] = useState(DEFAULT_FORM_VALUES)
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // Get home page (create if doesn't exist)
  const homePage = homePagesData?.results?.[0]
  const homePageId = homePage?.id
  
  // Get content pages
  const contentPages = contentPagesData?.results || []
  
  // Process navigation items with grid positions
  const navigationItems: NavigationWithPosition[] = (homePage?.navigations || [])
    .map((hpn: EventHomePageNavigation) => {
      const nav = typeof hpn.navigation === 'object' ? hpn.navigation : null
      if (!nav) return null
      return {
        ...nav,
        gridId: hpn.id,
        position_x: hpn.position_x || 1,
        position_y: hpn.position_y || 1,
        col_span: hpn.col_span || 1,
        row_span: hpn.row_span || 1,
      } as NavigationWithPosition
    })
    .filter((item): item is NavigationWithPosition => item !== null)
  
  // Create home page if it doesn't exist
  useEffect(() => {
    if (!homePagesLoading && homePagesData && homePagesData.results.length === 0) {
      createHomePage.mutate({ event: eventIdNum })
    }
  }, [homePagesLoading, homePagesData, eventIdNum])
  
  // Permission guard
  if (!canEditHomeLayout) {
    return <AccessDenied message="You don't have permission to edit the home layout." />
  }
  
  const isLoading = homePagesLoading || navigationsLoading
  
  // Open add modal with optional pre-filled position
  const openAddModal = (posX?: number, posY?: number) => {
    setFormData({
      ...DEFAULT_FORM_VALUES,
      position_x: posX || 1,
      position_y: posY || 1,
    })
    setShowAddModal(true)
  }
  
  // Open edit modal
  const openEditModal = (item: NavigationWithPosition) => {
    setEditingItem(item)
    setFormData({
      title: item.title,
      link_type: item.link_type,
      external_url: item.external_url || '',
      content_page: item.content_page,
      eventpay_function: item.eventpay_function,
      event_category: item.event_category ?? null,
      position_x: item.position_x,
      position_y: item.position_y,
      col_span: item.col_span,
      row_span: item.row_span,
      background_color: item.background_color || '#374151',
      text_color: item.text_color || '#ffffff',
      background_image: item.background_image || '',
      is_published: item.is_published,
      hide_title: item.hide_title,
    })
    setShowEditModal(true)
  }
  
  // Handle cell click in grid
  const handleCellClick = (col: number, row: number) => {
    // Check if cell is occupied
    const occupiedItem = navigationItems.find(item => 
      item.position_x === col && item.position_y === row
    )
    
    if (occupiedItem) {
      openEditModal(occupiedItem)
    } else if (canAddNavigationItem) {
      openAddModal(col, row)
    }
  }
  
  // Handle form submit for creating new item
  const handleCreate = async () => {
    if (!homePageId || !formData.title) return
    
    setIsSubmitting(true)
    try {
      // Create the navigation item
      const newNav = await createNavigation.mutateAsync({
        event: eventIdNum,
        title: formData.title,
        link_type: formData.link_type,
        external_url: formData.link_type === 'external_url' ? formData.external_url : null,
        content_page: formData.link_type === 'content_page' ? formData.content_page : null,
        eventpay_function: formData.link_type === 'eventpay_function' ? formData.eventpay_function : null,
        event_category: formData.link_type === 'eventpay_function' && formData.eventpay_function === 'vendors' ? formData.event_category : null,
        background_color: formData.background_color,
        text_color: formData.text_color,
        background_image: formData.background_image || null,
        is_published: formData.is_published,
        hide_title: formData.hide_title,
      })
      
      // Deferred image from the create form — the id it needs now exists.
      if (pendingImage) {
        try {
          await uploadNavigationImage.mutateAsync({
            navigationId: newNav.id,
            eventId: eventIdNum,
            file: pendingImage,
          })
        } catch (error) {
          // The item was created; the image can be added from Edit —
          // don't fail the whole create over it.
          console.error('Failed to upload image for new item:', error)
        }
      }

      // Link to home page with position
      await createHomePageNavigation.mutateAsync({
        home_page: homePageId,
        navigation: newNav.id,
        position_x: formData.position_x,
        position_y: formData.position_y,
        col_span: formData.col_span,
        row_span: formData.row_span,
        eventId: eventIdNum,
      })

      // Item must be visible the moment the modal closes.
      await refetchHomeLayout()

      setShowAddModal(false)
      setFormData(DEFAULT_FORM_VALUES)
      setPendingImage(null)
    } catch (error) {
      console.error('Failed to create navigation item:', error)
    } finally {
      setIsSubmitting(false)
    }
  }
  
  // Handle form submit for updating item
  const handleUpdate = async () => {
    if (!editingItem || !formData.title) return
    
    setIsSubmitting(true)
    try {
      // Update the navigation item
      await updateNavigation.mutateAsync({
        navigationId: editingItem.id,
        eventId: eventIdNum,
        data: {
          title: formData.title,
          link_type: formData.link_type,
          external_url: formData.link_type === 'external_url' ? formData.external_url : null,
          content_page: formData.link_type === 'content_page' ? formData.content_page : null,
          eventpay_function: formData.link_type === 'eventpay_function' ? formData.eventpay_function : null,
          event_category: formData.link_type === 'eventpay_function' && formData.eventpay_function === 'vendors' ? formData.event_category : null,
          background_color: formData.background_color,
          text_color: formData.text_color,
          background_image: formData.background_image || null,
          is_published: formData.is_published,
          hide_title: formData.hide_title,
        },
      })
      
      // Update grid position if changed
      if (editingItem.gridId && homePageId) {
        await updateHomePageNavigation.mutateAsync({
          id: editingItem.gridId,
          homePageId,
          eventId: eventIdNum,
          data: {
            position_x: formData.position_x,
            position_y: formData.position_y,
            col_span: formData.col_span,
            row_span: formData.row_span,
          },
        })
      }

      // Changes must be visible the moment the modal closes.
      await refetchHomeLayout()

      setShowEditModal(false)
      setEditingItem(null)
    } catch (error) {
      console.error('Failed to update navigation item:', error)
    } finally {
      setIsSubmitting(false)
    }
  }
  
  // Handle delete
  const handleDelete = async (item: NavigationWithPosition) => {
    if (!confirm('Are you sure you want to delete this navigation item?')) return
    
    try {
      // Delete from home page first
      if (item.gridId && homePageId) {
        await deleteHomePageNavigation.mutateAsync({
          id: item.gridId,
          homePageId,
          eventId: eventIdNum,
        })
      }
      
      // Delete the navigation item
      await deleteNavigation.mutateAsync({
        navigationId: item.id,
        eventId: eventIdNum,
      })
    } catch (error) {
      console.error('Failed to delete navigation item:', error)
    }
  }
  
  // Format link type for display
  const formatLinkType = (type: NavigationLinkType) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
            Home Layout
          </h1>
          {event && (
            <p className="text-neutral-600 dark:text-neutral-400 mt-1">
              Configure the homepage layout for {event.name}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowGuidelines(true)}>
            <Layout className="w-4 h-4 mr-2" />
            Image Guidelines
          </Button>
          {canAddNavigationItem && (
            <Button onClick={() => openAddModal()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-5">
          {/* Configuration Panel (Left 60%) */}
          <div className="lg:col-span-3 space-y-6">
            {/* Navigation Items List */}
            <div className="px-2">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white flex items-center">
                  <Navigation className="w-5 h-5 mr-2" />
                  Navigation Items
                  <span className="ml-2 text-sm font-normal text-neutral-500">
                    ({navigationItems.length})
                  </span>
                </h3>
              </div>

              <div className="space-y-3">
                {navigationItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
                  >
                    <div className="flex items-center space-x-4 min-w-0">
                      {/* Color Preview */}
                      <div
                        className="w-12 h-12 rounded-lg shrink-0 flex items-center justify-center text-white text-xs font-medium shadow-sm bg-cover bg-center"
                        style={{ 
                          backgroundColor: item.background_color || '#374151',
                          backgroundImage: item.background_image ? `url(${item.background_image})` : undefined,
                        }}
                      >
                        {!item.background_image && (
                          <span>{item.title?.substring(0, 2).toUpperCase() || 'NA'}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-neutral-900 dark:text-white truncate">
                          {item.title}
                        </h4>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400">
                          <span className="inline-flex items-center">
                            <Grid className="w-3 h-3 mr-1" />
                            ({item.position_x}, {item.position_y})
                          </span>
                          <span className="mx-2">•</span>
                          <span>{item.col_span}×{item.row_span}</span>
                          <span className="mx-2">•</span>
                          <span className="capitalize">{formatLinkType(item.link_type)}</span>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 sm:shrink-0">
                      {/* Published Badge */}
                      {item.is_published ? (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 rounded-full mr-2">
                          Published
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-400 rounded-full mr-2">
                          Draft
                        </span>
                      )}
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditModal(item)}
                        title="Edit item"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      {canDeleteNavigationItem && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(item)}
                          title="Delete item"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                
                {/* Empty State */}
                {navigationItems.length === 0 && (
                  <div className="text-center py-12 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg border-2 border-dashed border-neutral-300 dark:border-neutral-700">
                    <Layout className="w-12 h-12 mx-auto mb-4 text-neutral-400" />
                    <p className="text-lg font-medium text-neutral-600 dark:text-neutral-300 mb-2">
                      No navigation items yet
                    </p>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
                      Create your first navigation tile for the event homepage.
                    </p>
                    {canAddNavigationItem && (
                      <Button onClick={() => openAddModal()}>
                        <Plus className="w-4 h-4 mr-1" />
                        Add First Item
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Visual Grid Editor */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Grid className="w-5 h-5 mr-2" />
                  Visual Editor
                  <span className="ml-2 text-sm font-normal text-neutral-500">2×6 Grid</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border border-neutral-300 dark:border-neutral-600 rounded-lg p-4 bg-neutral-50 dark:bg-neutral-900">
                  <div 
                    className="grid grid-cols-2 gap-3 relative"
                    style={{ gridTemplateRows: `repeat(${GRID_MAX_ROWS}, 80px)` }}
                  >
                    {/* Background grid cells */}
                    {GRID_ROW_NUMBERS.map(row => (
                      [1, 2].map(col => (
                        <div
                          key={`bg-${col}-${row}`}
                          className="border border-neutral-300 dark:border-neutral-600 rounded bg-white dark:bg-neutral-800 
                                   flex items-center justify-center text-xs text-neutral-400 dark:text-neutral-500
                                   hover:border-blue-400 dark:hover:border-blue-500 transition-colors cursor-pointer"
                          style={{ gridColumn: col, gridRow: row }}
                          onClick={() => handleCellClick(col, row)}
                        >
                          {col},{row}
                        </div>
                      ))
                    ))}
                    
                    {/* Navigation items */}
                    {navigationItems.map(item => (
                      <div
                        key={`item-${item.id}`}
                        className="rounded text-white p-2 text-xs font-medium cursor-pointer 
                                  hover:opacity-90 transition-opacity flex flex-col justify-between bg-cover bg-center
                                  border-2 border-blue-500/50 z-10"
                        style={{
                          gridColumn: `${item.position_x} / span ${item.col_span}`,
                          gridRow: `${item.position_y} / span ${item.row_span}`,
                          backgroundColor: item.background_color || '#374151',
                          backgroundImage: item.background_image ? `url(${item.background_image})` : undefined,
                          color: item.text_color || '#ffffff',
                        }}
                        onClick={() => openEditModal(item)}
                      >
                        <div className="truncate font-semibold drop-shadow-md">{item.title}</div>
                        <div className="text-[10px] opacity-75 drop-shadow-md">
                          {item.col_span}×{item.row_span}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Legend */}
                  <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                    <div className="flex flex-wrap items-center gap-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 bg-blue-600 rounded"></div>
                        <span>Navigation Item</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 rounded"></div>
                        <span>Empty Cell</span>
                      </div>
                    </div>
                    <div className="text-xs text-neutral-500 dark:text-neutral-400 sm:text-right">
                      Click empty cell to add • Click item to edit
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Preview Panel (Right 40%) */}
          <div className="lg:col-span-2">
            <div className="sticky top-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Smartphone className="w-5 h-5 mr-2" />
                    Mobile Preview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Mobile Frame */}
                  <div className="mx-auto" style={{ maxWidth: '320px' }}>
                    <div className="bg-neutral-900 rounded-[2rem] p-2">
                      <div className="bg-black rounded-[1.5rem] overflow-hidden">
                        <div className="bg-neutral-50 dark:bg-neutral-900" style={{ height: '550px', overflowY: 'auto' }}>
                          {/* Header */}
                          <div className="bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700 p-3 sticky top-0 z-20">
                            <div className="flex items-center justify-between">
                              <h1 className="text-base font-bold text-neutral-900 dark:text-white truncate">
                                {event?.name || 'Event Name'}
                              </h1>
                              <Search className="w-5 h-5 text-neutral-600 dark:text-neutral-400 shrink-0" />
                            </div>
                          </div>
                          
                          {/* Navigation Grid */}
                          <div className="p-3">
                            <div 
                              className="grid grid-cols-2 gap-2"
                              style={{ gridTemplateRows: `repeat(${GRID_MAX_ROWS}, 80px)` }}
                            >
                              {navigationItems
                                .filter(item => item.is_published)
                                .map(item => (
                                  <div
                                    key={`preview-${item.id}`}
                                    className="rounded-lg overflow-hidden shadow-md relative"
                                    style={{
                                      backgroundColor: item.background_color || '#374151',
                                      color: item.text_color || '#ffffff',
                                      gridColumn: `${item.position_x} / span ${item.col_span}`,
                                      gridRow: `${item.position_y} / span ${item.row_span}`,
                                    }}
                                  >
                                    {/* Background Image */}
                                    {item.background_image && (
                                      <div 
                                        className="absolute inset-0 bg-cover bg-center"
                                        style={{ backgroundImage: `url(${item.background_image})` }}
                                      />
                                    )}
                                    
                                    <div className="relative z-10 h-full flex items-end p-2">
                                      {!item.hide_title && (
                                        <span className="font-semibold text-xs drop-shadow-lg">
                                          {item.title}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                            </div>
                            
                            {/* Empty preview state */}
                            {navigationItems.filter(i => i.is_published).length === 0 && (
                              <div className="flex items-center justify-center h-64 text-neutral-400 text-sm">
                                No published items
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* Add Item Modal */}
      {showAddModal && (
        <NavigationModal
          title="Add Navigation Item"
          formData={formData}
          setFormData={setFormData}
          contentPages={contentPages}
          onClose={() => { setShowAddModal(false); setPendingImage(null) }}
          onSubmit={handleCreate}
          isSubmitting={isSubmitting}
          submitLabel="Create Item"
          eventId={eventIdNum}
          pendingImage={pendingImage}
          onPendingImage={setPendingImage}
          allItems={navigationItems}
        />
      )}

      {/* Edit Item Modal */}
      {showEditModal && editingItem && (
        <NavigationModal
          title="Edit Navigation Item"
          formData={formData}
          setFormData={setFormData}
          contentPages={contentPages}
          onClose={() => {
            setShowEditModal(false)
            setEditingItem(null)
          }}
          onSubmit={handleUpdate}
          isSubmitting={isSubmitting}
          submitLabel="Save Changes"
          navigationId={editingItem.id}
          eventId={eventIdNum}
          allItems={navigationItems}
          editingId={editingItem.id}
        />
      )}

      {/* Image Guidelines Modal */}
      {showGuidelines && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowGuidelines(false)}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div 
              className="relative w-full max-w-2xl bg-white dark:bg-neutral-900 rounded-xl shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-5 border-b border-neutral-200 dark:border-neutral-800">
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
                  <Layout className="w-5 h-5 text-blue-600" />
                  Image Guidelines
                </h2>
                <button
                  type="button"
                  onClick={() => setShowGuidelines(false)}
                  className="p-2 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-5 space-y-6">
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  Navigation tiles use background images that fill the entire tile area. Use the recommended dimensions below for the sharpest results on all devices.
                </p>

                {/* Size Grid */}
                <div className="grid grid-cols-2 gap-4">
                  {/* 1x1 */}
                  <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded bg-blue-100 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 flex items-center justify-center">
                        <div className="w-4 h-4 rounded-sm bg-blue-500" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">1 &times; 1</p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">Single tile</p>
                      </div>
                    </div>
                    <div className="bg-neutral-50 dark:bg-neutral-800 rounded-md px-3 py-2">
                      <p className="text-sm font-mono font-medium text-neutral-900 dark:text-neutral-100">870 &times; 600 px</p>
                    </div>
                  </div>

                  {/* 2x1 */}
                  <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded bg-purple-100 dark:bg-purple-900/40 border border-purple-200 dark:border-purple-800 flex items-center justify-center">
                        <div className="w-6 h-3 rounded-sm bg-purple-500" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">2 &times; 1</p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">Wide tile</p>
                      </div>
                    </div>
                    <div className="bg-neutral-50 dark:bg-neutral-800 rounded-md px-3 py-2">
                      <p className="text-sm font-mono font-medium text-neutral-900 dark:text-neutral-100">1795 &times; 615 px</p>
                    </div>
                  </div>

                  {/* 1x2 */}
                  <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded bg-green-100 dark:bg-green-900/40 border border-green-200 dark:border-green-800 flex items-center justify-center">
                        <div className="w-3 h-6 rounded-sm bg-green-500" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">1 &times; 2</p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">Tall tile</p>
                      </div>
                    </div>
                    <div className="bg-neutral-50 dark:bg-neutral-800 rounded-md px-3 py-2">
                      <p className="text-sm font-mono font-medium text-neutral-900 dark:text-neutral-100">870 &times; 1260 px</p>
                    </div>
                  </div>

                  {/* 2x2 */}
                  <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded bg-amber-100 dark:bg-amber-900/40 border border-amber-200 dark:border-amber-800 flex items-center justify-center">
                        <div className="w-5 h-5 rounded-sm bg-amber-500" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">2 &times; 2</p>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400">Large tile</p>
                      </div>
                    </div>
                    <div className="bg-neutral-50 dark:bg-neutral-800 rounded-md px-3 py-2">
                      <p className="text-sm font-mono font-medium text-neutral-900 dark:text-neutral-100">1795 &times; 1260 px</p>
                    </div>
                  </div>
                </div>

                {/* Tips */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2">Tips</h4>
                  <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1.5">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-0.5">&bull;</span>
                      Use <strong>PNG</strong> or <strong>JPG</strong> format for best compatibility
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-0.5">&bull;</span>
                      Images are displayed as <strong>cover</strong> &mdash; they fill the tile and crop edges if needed
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-0.5">&bull;</span>
                      Keep important content <strong>centered</strong> to avoid cropping on different screen sizes
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-500 mt-0.5">&bull;</span>
                      If the title is visible, ensure sufficient <strong>contrast</strong> between the text and the image
                    </li>
                  </ul>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end p-5 border-t border-neutral-200 dark:border-neutral-800">
                <Button onClick={() => setShowGuidelines(false)}>
                  Got it
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Navigation Item Modal Component
interface NavigationModalProps {
  title: string
  formData: typeof DEFAULT_FORM_VALUES
  setFormData: React.Dispatch<React.SetStateAction<typeof DEFAULT_FORM_VALUES>>
  contentPages: ContentPage[]
  onClose: () => void
  onSubmit: () => void
  isSubmitting: boolean
  submitLabel: string
  navigationId?: number
  eventId?: number
  /** Create mode: the image upload endpoint needs a navigation id, so the
   * file is held here and uploaded right after the item is created. */
  pendingImage?: File | null
  onPendingImage?: (file: File | null) => void
  /** All tiles + the id being edited — powers the position picker's occupancy. */
  allItems: NavigationWithPosition[]
  editingId?: number | null
}

function NavigationModal({
  title,
  formData,
  setFormData,
  contentPages,
  onClose,
  onSubmit,
  isSubmitting,
  submitLabel,
  navigationId,
  eventId,
  pendingImage,
  onPendingImage,
  allItems,
  editingId,
}: NavigationModalProps) {
  const modalRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const uploadImage = useUploadNavigationImage()
  // Vendor categories feed the category filter under the "Vendors" function
  const { data: eventCategoriesData } = useEventCategories(eventId ?? 0)
  const eventCategories: EventCategory[] = eventCategoriesData?.results ?? []

  // Local preview for a create-mode file (uploads after the item exists).
  const pendingPreview = useMemo(
    () => (pendingImage ? URL.createObjectURL(pendingImage) : null),
    [pendingImage],
  )
  useEffect(() => () => {
    if (pendingPreview) URL.revokeObjectURL(pendingPreview)
  }, [pendingPreview])
  
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])
  
  // Close on backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose()
  }
  
  // Handle image file upload
  const handleImageUpload = async (file: File) => {
    if (!navigationId || !eventId) return
    
    setIsUploading(true)
    try {
      const result = await uploadImage.mutateAsync({ navigationId, eventId, file })
      // Update form data with the new image URL
      if (result.background_image) {
        setFormData(prev => ({ ...prev, background_image: result.background_image || '' }))
      }
    } catch (error) {
      console.error('Failed to upload image:', error)
    } finally {
      setIsUploading(false)
    }
  }
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (navigationId && eventId) {
        // Edit mode: the record exists — upload immediately.
        handleImageUpload(file)
      } else {
        // Create mode: hold the file; the parent uploads it right after
        // the item is created (the endpoint needs the new id).
        onPendingImage?.(file)
      }
    }
    // Allow re-selecting the same file.
    e.target.value = ''
  }
  
  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4"
      onClick={handleBackdropClick}
    >
      <div 
        ref={modalRef}
        className="bg-white dark:bg-neutral-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200 dark:border-neutral-700">
          <h3 className="text-xl font-semibold text-neutral-900 dark:text-white">{title}</h3>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Modal Body */}
        <div className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <Input
              value={formData.title}
              onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Food Menu, My Wallet"
              required
            />
          </div>
          
          {/* Link Type + its value on one row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:items-start">
            {/* Link Type */}
            <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Link Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.link_type}
              onChange={e => setFormData(prev => ({ 
                ...prev, 
                link_type: e.target.value as NavigationLinkType,
                external_url: '',
                content_page: null,
                eventpay_function: null,
              }))}
              className="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg 
                       bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white
                       focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {LINK_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            </div>
            {/* dependent value — fills the second column */}
            <div>
          
          {/* External URL (conditional) */}
          {formData.link_type === 'external_url' && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">External URL</label>
              <Input
                type="url"
                value={formData.external_url}
                onChange={e => setFormData(prev => ({ ...prev, external_url: e.target.value }))}
                placeholder="https://example.com"
              />
            </div>
          )}
          
          {/* Content Page (conditional) */}
          {formData.link_type === 'content_page' && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Content Page</label>
              <select
                value={formData.content_page || ''}
                onChange={e => setFormData(prev => ({ 
                  ...prev, 
                  content_page: e.target.value ? Number(e.target.value) : null 
                }))}
                className="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg 
                         bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a content page...</option>
                {contentPages.map(page => (
                  <option key={page.id} value={page.id}>{page.title}</option>
                ))}
              </select>
              {eventId && (
                <Link 
                  to="/events/$eventId/event-content/create"
                  params={{ eventId: String(eventId) }}
                  className="inline-flex items-center gap-1 mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  <Plus className="w-4 h-4" />
                  Add new Content Page
                </Link>
              )}
              {contentPages.length === 0 && !eventId && (
                <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                  No content pages available. Create one in Event Content first.
                </p>
              )}
            </div>
          )}
          
          {/* EventPay Function (conditional) */}
          {formData.link_type === 'eventpay_function' && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">EventPay Function</label>
              <select
                value={formData.eventpay_function || ''}
                onChange={e => {
                  const fn = e.target.value as EventPayFunction || null
                  setFormData(prev => ({
                    ...prev,
                    eventpay_function: fn,
                    event_category: fn === 'vendors' ? prev.event_category : null,
                  }))
                }}
                className="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg 
                         bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a function...</option>
                {EVENTPAY_FUNCTIONS.map(func => (
                  <option key={func.value} value={func.value}>{func.label}</option>
                ))}
              </select>

              {/* Category filter (vendors function only) */}
              {formData.eventpay_function === 'vendors' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Vendor Category</label>
                  <select
                    value={formData.event_category ?? ''}
                    onChange={e => setFormData(prev => ({
                      ...prev,
                      event_category: e.target.value ? Number(e.target.value) : null,
                    }))}
                    className="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg 
                             bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white
                             focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All vendors (no filter)</option>
                    {eventCategories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  {eventCategories.length === 0 && (
                    <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                      No vendor categories exist for this event yet, so this tile opens the full vendors list.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
            </div>
          </div>
          
          {/* Position + Size — side by side */}
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-5 sm:items-start">
            <div className="sm:col-span-3">
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Position <span className="font-normal text-neutral-400">— tap where the tile starts</span>
            </label>
            <GridPositionPicker
              posX={formData.position_x}
              posY={formData.position_y}
              colSpan={formData.col_span}
              rowSpan={formData.row_span}
              maxRows={GRID_MAX_ROWS}
              items={allItems}
              editingId={editingId ?? null}
              onPick={(col, row) =>
                setFormData(prev => {
                  const newX = prev.col_span >= 2 ? 1 : col
                  const newY = Math.min(row, Math.max(1, GRID_MAX_ROWS - prev.row_span + 1))
                  return { ...prev, position_x: newX, position_y: newY }
                })
              }
            />
            <p className="mt-1.5 text-xs text-neutral-500 dark:text-neutral-400">
              {formData.position_x === 1 ? 'Left' : 'Right'} column, row {formData.position_y}
              {(formData.col_span > 1 || formData.row_span > 1) &&
                ` · spans ${formData.col_span}×${formData.row_span}`}
            </p>
          </div>
          
          {/* Size — beside the grid, with live colour gauges */}
            <div className="sm:col-span-2 space-y-4">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Width <WidthGauge value={formData.col_span} /></label>
              <select
                value={formData.col_span}
                onChange={e => setFormData(prev => { const cs = Number(e.target.value); return { ...prev, col_span: cs, position_x: cs >= 2 ? 1 : prev.position_x } })}
                className="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg 
                         bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={1}>1 Column</option>
                <option value={2}>2 Columns (Full Width)</option>
              </select>
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Height <HeightGauge value={formData.row_span} max={3} /></label>
              <select
                value={formData.row_span}
                onChange={e => setFormData(prev => { const rs = Number(e.target.value); return { ...prev, row_span: rs, position_y: Math.min(prev.position_y, Math.max(1, GRID_MAX_ROWS - rs + 1)) } })}
                className="w-full px-4 py-2.5 border border-neutral-300 dark:border-neutral-600 rounded-lg 
                         bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={1}>1 Row</option>
                <option value={2}>2 Rows</option>
                <option value={3}>3 Rows</option>
              </select>
            </div>

          {/* Background Image */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Background Image</label>
            {formData.background_image && (
              <div className="relative block mb-3">
                <img 
                  src={formData.background_image} 
                  className="w-full h-24 rounded-lg object-cover" 
                  alt="Preview"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none'
                  }}
                />
                <button 
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, background_image: '' }))}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                >
                  ×
                </button>
              </div>
            )}
            {!formData.background_image && pendingPreview && (
              <div className="relative block mb-3">
                <img
                  src={pendingPreview}
                  className="w-full h-24 rounded-lg object-cover"
                  alt="Selected"
                />
                <button
                  type="button"
                  onClick={() => onPendingImage?.(null)}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                >
                  ×
                </button>
              </div>
            )}
            <div className="flex flex-col items-start gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                {isUploading ? 'Uploading...' : 'Choose Image'}
              </Button>
              <span className="text-sm text-neutral-500 dark:text-neutral-400">
                {navigationId && eventId
                  ? formData.background_image ? 'Image uploaded' : 'No image selected'
                  : pendingImage
                    ? `${pendingImage.name} — uploads when the item is created`
                    : 'No image selected'}
              </span>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          </div>
            </div>
          </div>

          {/* Colors */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Background Color</label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={formData.background_color}
                  onChange={e => setFormData(prev => ({ ...prev, background_color: e.target.value }))}
                  className="w-12 h-10 rounded border border-neutral-300 dark:border-neutral-600 cursor-pointer"
                />
                <Input
                  value={formData.background_color}
                  onChange={e => setFormData(prev => ({ ...prev, background_color: e.target.value }))}
                  placeholder="#374151"
                  className="flex-1"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Text Color</label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={formData.text_color}
                  onChange={e => setFormData(prev => ({ ...prev, text_color: e.target.value }))}
                  className="w-12 h-10 rounded border border-neutral-300 dark:border-neutral-600 cursor-pointer"
                />
                <Input
                  value={formData.text_color}
                  onChange={e => setFormData(prev => ({ ...prev, text_color: e.target.value }))}
                  placeholder="#ffffff"
                  className="flex-1"
                />
              </div>
            </div>
          </div>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 -mt-2">
            Background color shows when no image is set or as a fallback
          </p>
          
          {/* Options */}
          <div className="flex items-center space-x-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_published}
                onChange={e => setFormData(prev => ({ ...prev, is_published: e.target.checked }))}
                className="w-4 h-4 text-blue-600 border-neutral-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-neutral-700 dark:text-neutral-300">Published</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.hide_title}
                onChange={e => setFormData(prev => ({ ...prev, hide_title: e.target.checked }))}
                className="w-4 h-4 text-blue-600 border-neutral-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-neutral-700 dark:text-neutral-300">Hide Title</span>
            </label>
          </div>
          
          {/* Preview */}
          <div className="pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">Preview</label>
            <div className="flex justify-center">
              <div 
                className="w-32 h-20 rounded-lg shadow-lg flex items-end p-2 bg-cover bg-center relative overflow-hidden"
                style={{
                  backgroundColor: formData.background_color,
                  color: formData.text_color,
                  backgroundImage: formData.background_image ? `url(${formData.background_image})` : undefined,
                }}
              >
                {!formData.hide_title && (
                  <span className="text-xs font-semibold truncate drop-shadow-md relative z-10">
                    {formData.title || 'Title'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Modal Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900/50 rounded-b-xl">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={isSubmitting || !formData.title}>
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            {submitLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}
